export const DocumentChecklistListContent = {
  checklistHeader: `Simplify your inheritance with our custom checklist`,
  checklistSubHeader: `Check each of the boxes preceding the <span class="c11n-text-md-bold">Get started button</span> to continue.`,
  requiredCheckboxesErrorBanner: {
    heading: `Please check all of the required checkboxes before getting started.`,
    body: `In order to make sure you're prepared to begin your inheritance transfer, please review the required checkboxes before continuing.`,
  },
  missingAccountTypeBanner: {
    heading: `We're still building checklists for all inheritance scenarios`,
    body1: `If you don't see one of your account types, don't worry, we're working it.`,
    body2Before: `For now, use the available checklist options that apply to your situation. If none apply, you can skip the checklist and get started by selecting`,
    body2Link: `Start your inheritance transfer`,
    body2After: `.`,
    buttonLabel: `Start your inheritance transfer`,
  },
  completeYourTransferBanner: {
    heading: `Complete your transfer`,
    buttonLabel: `Get started`,
  },
  trustUnderAgreementBanner: {
    id: 'trust-under-agreement-banner',
    title: 'Update your trust after a loss',
    text: 'If you’re just looking to update trust information due to the loss of a grantor or trustee, you’ll need to use a different process. ',
    url: 'https://support.vanguard.com/change-account-ownership/trust-changes-due-to-death',
    linkText: 'Visit our support page for more information.',
    tags: ['IRA_TRUSTEE_AGREEMENT'],
  },
  requiredChecklist: [
    {
      title:
        'If there are multiple beneficiaries, make sure they’re all claiming their portion of the inheritance.',
      id: 'multiple-beneficiaries',
      controlName: 'multipleBeneficiaries',
      tags: ['IRA', 'INDV'],
      content: [
        {
          type: 'TEXT',
          id: 'multiple-beneficiaries-not-claiming',
          text: 'If a beneficiary isn’t claiming, they need to notify Vanguard before any other beneficiaries complete this process.',
          tags: ['IRA', 'INDV'],
        },
      ],
    },
    {
      title: 'Gather the required documents you’ll need to upload',
      id: 'documents',
      controlName: 'documents',
      tags: ['ALL'],
      content: [
        {
          type: 'LIST',
          id: 'documents-list',
          tags: ['ALL'],
          items: [
            {
              type: 'LIST_ITEM_TEXT',
              id: 'documents-decedent-death-certificate',
              text: 'Decedent’s death certificate',
              tags: ['ALL'],
            },
            {
              id: 'documents-letters-of-testamentary',
              type: 'LIST_ITEM_TOOLTIP',
              text: 'Letters of testamentary, letters of administration, or short certificate (dated within one year)',
              tooltipId: 'documents-letters-of-testamentary-tooltip',
              tooltip: 'Estate documents can be provided by the decedent’s local courthouse.',
              tags: ['EXECUTOR'],
            },
            {
              type: 'LIST_ITEM_TEXT',
              id: 'documents-beneficiary-death-certificate',
              text: 'Death certificates for any beneficiaries who have passed (if applicable)',
              tags: ['IRA_NAME_OR_RELATIONSHIP', 'INDV_OR_JOINT', 'EXECUTOR'],
            },
            {
              type: 'LIST_ITEM_TEXT',
              id: 'documents-trust-agreement',
              text: 'Pages of the trust agreement that contain the trust name, trust date, and trustees’ names',
              tags: ['IRA_TRUSTEE_AGREEMENT'],
            },
            {
              type: 'LIST_ITEM_TEXT',
              id: 'documents-signature-pages',
              text: 'Signature pages signed by the persons who were required to sign under state law',
              tags: ['IRA_TRUSTEE_AGREEMENT'],
            },
            {
              type: 'LIST_ITEM_TEXT',
              id: 'documents-letters-of-resignation',
              text: 'Letters of resignation or death certificates for trustees who are resigning or deceased',
              tags: ['IRA_TRUSTEE_AGREEMENT'],
            },
            {
              type: 'LIST_ITEM_TEXT',
              id: 'documents-sub-trust',
              text: 'For a sub-trust (e.g., marital trust, bypass trust, or generation-skipping trust), pages that contain the provision outlining the creation of the sub-trust and trustees’ names',
              tags: ['IRA_TRUSTEE_AGREEMENT'],
            },
            {
              type: 'LIST_ITEM_TEXT',
              id: 'documents-will-pages',
              text: 'Pages of the will that contain the trust name, trustees’ names, creation of the trust, and testator’s signature',
              tags: ['IRA_TRUSTEE_WILL'],
            },
            {
              type: 'LIST_ITEM_TEXT',
              id: 'documents-will-evidence',
              text: 'Evidence that the will was filed with the probate court (e.g., a court stamp, letters of testamentary*, or court document appointing the trustees)',
              tags: ['IRA_TRUSTEE_WILL'],
            },
          ],
        },
        {
          type: 'BANNER_TEXT',
          id: 'documents-banner',
          text: 'If the decedent lived in New Jersey or Pennsylvania at the time of death, we may need to collect Form L-8 (NJ) or an Inheritance Tax Waiver. Visit the department of revenue website for your state if you’re unsure.',
          tags: ['ALL'],
        },
      ],
    },
    {
      title: 'Have the decedent’s information ready',
      id: 'decedent',
      controlName: 'decedent',
      tags: ['ALL'],
      content: [
        {
          type: 'LIST',
          id: 'decedent-list',
          tags: ['ALL'],
          items: [
            {
              type: 'LIST_ITEM_TEXT',
              id: 'decedent-ssn',
              text: 'Social Security number (SSN)',
              tags: ['ALL'],
            },
            {
              type: 'LIST_ITEM_TEXT',
              id: 'decedent-date-of-birth',
              text: 'Date of birth',
              tags: ['ALL'],
            },
            {
              type: 'LIST_ITEM_TEXT',
              id: 'decedent-date-of-death',
              text: 'Date of death',
              tags: ['ALL'],
            },
          ],
        },
      ],
    },
    {
      title: 'Have the names and birth dates for all other beneficiaries ready',
      id: 'other-beneficiary-info',
      controlName: 'otherBeneficiaryInfo',
      tags: ['IRA_RELATIONSHIP_NOT_MARRIED'],
      content: [],
    },
    {
      title: 'Gather the following estate information',
      id: 'estate',
      controlName: 'estate',
      tags: ['EXECUTOR'],
      content: [
        {
          type: 'LIST',
          id: 'estate-list',
          tags: ['EXECUTOR'],
          items: [
            {
              type: 'LIST_ITEM_TEXT',
              id: 'estate-name',
              text: 'Estate name',
              tags: ['EXECUTOR'],
            },
            {
              id: 'estate-tax-identification',
              type: 'LIST_ITEM_TOOLTIP',
              text: 'Tax identification number',
              tooltipId: 'estate-tax-identification-tooltip',
              tooltip:
                'If you need to apply for a new tax identification number, such as an EIN, you can do so on the IRS website.',
              tags: ['EXECUTOR'],
            },
          ],
        },
        {
          type: 'BANNER_TEXT',
          id: 'estate-banner',
          text: 'If the estate was established outside of the United States, you’ll need to call in to complete your request.',
          tags: ['EXECUTOR'],
        },
      ],
    },
    {
      title: 'Gather the following trust information',
      id: 'trust',
      controlName: 'trust',
      tags: ['IRA_TRUSTEE'],
      content: [
        {
          type: 'LIST',
          id: 'trust-list',
          tags: ['IRA_TRUSTEE'],
          items: [
            {
              type: 'LIST_ITEM_TEXT',
              id: 'trust-name',
              text: 'Trust name',
              tags: ['IRA_TRUSTEE'],
            },
            {
              type: 'LIST_ITEM_TEXT',
              id: 'trust-date',
              text: 'Trust date',
              tags: ['IRA_TRUSTEE'],
            },
            {
              type: 'LIST_ITEM_TEXT',
              id: 'grantors-name',
              text: 'Grantor’s name',
              tags: ['IRA_TRUSTEE'],
            },
            {
              id: 'trust-tax-identification',
              type: 'LIST_ITEM_TOOLTIP',
              text: 'Tax identification number (SSN or EIN)',
              tooltipId: 'trust-tax-identification-tooltip',
              tooltip:
                'If you need to apply for a new tax identification number, such as an EIN, you can do so on the IRS website.',
              tags: ['IRA_TRUSTEE'],
            },
          ],
        },
        {
          type: 'BANNER_TEXT',
          id: 'trust-banner',
          text: 'If the trust was established outside of the United States, you’ll need to call us to complete your request.',
          tags: ['IRA_TRUSTEE'],
        },
      ],
    },
    {
      title: 'Complete the digital inheritance request',
      id: 'digital-inheritance',
      controlName: 'digitalInheritance',
      tags: ['ALL'],
      content: [
        {
          type: 'TEXT',
          id: 'digital-inheritance-text',
          text: 'You’ll',
          tags: ['ALL'],
        },
        {
          type: 'LIST',
          id: 'digital-inheritance-list',
          tags: ['ALL'],
          items: [
            {
              type: 'LIST_ITEM_TEXT',
              id: 'digital-inheritance-online-access',
              text: 'Set up online access (if you’re new to Vanguard) or log in to your account',
              tags: ['ALL'],
            },
            {
              type: 'LIST_ITEM_TEXT',
              id: 'digital-inheritance-decedent-info',
              text: 'Provide decedent information',
              tags: ['ALL'],
            },
            {
              id: 'digital-inheritance-uploading-documents',
              type: 'LIST_ITEM_TOOLTIP',
              text: 'Upload all necessary documents',
              tooltipId: 'digital-inheritance-uploading-documents-tooltip',
              tooltip:
                'For easy uploading, take a picture of the hard copy with a smartphone and email it to yourself.',
              tags: ['ALL'],
            },
            {
              type: 'LIST_ITEM_TEXT',
              id: 'digital-inheritance-other-beneficiaries',
              text: 'Fill out information for all other beneficiaries.',
              tags: ['IRA_RELATIONSHIP_NOT_MARRIED'],
            },
            {
              type: 'LIST_ITEM_TEXT',
              id: 'digital-inheritance-estate-information',
              text: 'Input estate information',
              tags: ['EXECUTOR'],
            },
            {
              type: 'LIST_ITEM_TEXT',
              id: 'digital-inheritance-trust-information',
              text: 'Input trust information',
              tags: ['IRA_TRUSTEE'],
            },
            {
              id: 'digital-inheritance-existing-accounts',
              type: 'LIST_ITEM_TEXT',
              text: 'Choose an existing account you want to transfer the assets into or open a new one to receive the assets. You’ll do this for each account type.',
              tags: ['ALL'],
            },
            {
              id: 'digital-inheritance-review-details',
              type: 'LIST_ITEM_TEXT',
              text: 'Review the details and sign off on the transfer. Processing typically takes between 5-7 business days.',
              tags: ['ALL'],
            },
          ],
        },
        {
          type: 'BANNER_TEXT',
          id: 'digital-inheritance-estate-banner',
          text: 'Your estate transfer will need to be completed in its own request. Once you’re finished, click "Inherit another account" on the confirmation page to take care of the rest.',
          tags: ['EXECUTOR'],
        },
        {
          type: 'BANNER_TEXT',
          id: 'digital-inheritance-trust-banner',
          text: 'Your trust transfer will need to be completed in its own request. Once you’re finished, click "Inherit another account" on the confirmation page to take care of the rest.',
          tags: ['IRA_TRUSTEE'],
        },
      ],
    },
  ],
  optionalChecklist: [
    {
      title: 'Consult with a tax advisor to see if you’re eligible for a cost basis step up',
      id: 'tax-advisor',
      controlName: 'taxAdvisor',
      tags: ['EXECUTOR', 'IRA_TRUSTEE'],
      content: [],
    },
    {
      title: 'Let the other beneficiaries know that they’ll need to complete this process too.',
      id: 'other-beneficiaries',
      controlName: 'otherBeneficiaries',
      tags: ['IRA_NAME_OR_RELATIONSHIP', 'INDV', 'EXECUTOR'],
      content: [
        {
          type: 'SPECIAL',
          id: 'other-beneficiaries-link',
          text1: 'Send them ',
          text2: ' to help them get started.',
          linkText: 'this link',
          url: 'https://support.vanguard.com/change-account-ownership/inheritance-transfer',
          tags: ['IRA_NAME_OR_RELATIONSHIP', 'INDV', 'EXECUTOR'],
        },
      ],
    },
    {
      title: 'Learn about how required minimum distributions (RMD’s) apply to your inherited IRA.',
      id: 'minimum-distribution',
      controlName: 'minimumDistribution',
      tags: ['IRA'],
      content: [
        {
          type: 'LINK',
          id: 'minimum-distribution-link',
          text: 'Read article',
          url: 'https://investor.vanguard.com/investor-resources-education/retirement/rmd-rules-for-inherited-iras',
          tags: ['IRA'],
        },
      ],
    },
  ],
};
